import type { Issue, ModelField, RecordValidator } from '@resource-kit/model';
import type { EnumItemsSource } from '../util/enum-items';
import { resolveTypeZodBase } from '../widgets/registry';
import { fieldValuePolicy } from './field-value-policy';

/**
 * The three validation layers of design §5.4 (#1797):
 *
 *   1. FIELD    — the field's own `schema`, else the field type's `zodBase`.
 *   2. RECORD   — `RecordValidator`, for cross-field constraints.
 *   3. VIEW     — per-item overrides (currently `required`).
 *
 * Every `Issue.message` is an i18n KEY, never a translated string — including
 * the ones derived from a backend 400/422, which normalise onto the same
 * `FieldPath` so they render through one path with no visual distinction. That
 * normalisation (`normalizeBackendIssues`) stays in the HOST, at
 * `src/lib/backend-issues.ts`: it reads `ApiError`, which is this
 * app's HTTP client's own error class and therefore not something a package can
 * name (ADR-0035 D1).
 */

/** Per-item view overrides. Only what a view can legitimately tighten. */
export interface ViewFieldOverride {
  required?: boolean;
}

/**
 * The members the field layer reads.
 *
 * Structural on purpose (#2167 PR-3, invariant 7). This used to take
 * `EntityField<T>`, which is a REACT contract — `render` is a `ComponentType` —
 * and naming it here would put `react/` inside the root export's closure and
 * make `import '@resource-kit/controllers'` a framework binding. Every member
 * type is copied verbatim from `EntityField`'s member of the same name
 * (`react/contracts.ts`), never widened, so `EntityField<T>` still satisfies it
 * and no call site changed.
 *
 * `key` is `string` rather than `EntityField`'s per-branch
 * `Extract<keyof T, string>` / `string`, which is that union's own type: the
 * entity branch's key is a `keyof T` narrowed to strings, and the virtual
 * branch's is free-form, so the union of the two IS `string`.
 *
 * `schema` is spelled as the MODEL's member rather than `ZodType<unknown>` for
 * the same reason `EntityField` does: this package must not name `zod`, which
 * is not in ADR-0035's closed set of bare specifiers.
 *
 * It EXTENDS `EnumItemsSource` — the two enum members — even though nothing in
 * this module reads them, because `validateFieldValue` FORWARDS the whole field
 * to `resolveTypeZodBase`, and the seeded `enum` implementation derives its
 * schema from that field's own option list. A parameter type that omitted them
 * would compile only by having the host widen the field again on the far side,
 * i.e. by hiding the forward rather than declaring it.
 */
export interface ValidatableField extends EnumItemsSource {
  key: string;
  type: ModelField['type'];
  required?: boolean;
  schema?: ModelField['schema'];
}

const REQUIRED_KEY = 'rk.resource.validation.required';

/**
 * The catch-all issue key. Exported because the host's `normalizeBackendIssues`
 * falls back to THIS key rather than to a literal of its own, which would be a
 * copy free to drift.
 *
 * Its `rk.` spelling is the package's own (#2170, ADR-0035 D7), and it is the
 * only key in this path that got one. The host's per-code map next to that
 * fallback still names HOST keys (`resource.validation.tooSmall` and friends),
 * and those keys do NOT stay on the host side at render time: they travel as
 * `issue.message` DATA into `FieldSlot`, inside the package, which hands them
 * to `t()`. So a package really does resolve host keys here, and no i18n guard
 * in this repo can see it — the key is never written as a literal in package
 * source. Left as an open gap by #2170 rather than a settled boundary; do not
 * read this comment as saying the split is complete.
 */
export const GENERIC_ISSUE_KEY = 'rk.resource.validation.invalidField';

export function validateFieldValue<T>(
  field: ValidatableField,
  value: unknown,
  override?: ViewFieldOverride,
): Issue<T> | null {
  // `FieldPath<T>`'s first segment is a deferred conditional while `T` is an
  // unresolved generic, so tsc cannot see that `field.key` satisfies it.
  // `EntityField<T>` already guarantees the key names a property of `T`
  // (or is an explicitly `virtual` synthetic column).
  const path = [field.key] as unknown as Issue<T>['path'];
  const required = override?.required ?? field.required ?? false;
  const empty = fieldValuePolicy(field.type).isEmpty(value);

  if (empty) {
    // An untouched optional field is not an error; a required one is, and
    // the type's zod base has nothing useful to say about `''`.
    return required ? { path, code: 'required', message: REQUIRED_KEY } : null;
  }

  const schema = field.schema ?? resolveTypeZodBase(field);
  if (!schema) return null;

  const result = schema.safeParse(value);
  if (result.success) return null;

  const issue = result.error.issues[0];
  return {
    path,
    code: issue?.code ?? 'invalid',
    message: issue?.message ?? GENERIC_ISSUE_KEY,
  };
}

/** Save-time sweep: every field's schema, one issue per offending field. */
export function validateDraft<T>(
  fields: readonly ValidatableField[],
  draft: Record<string, unknown>,
  overrides?: Readonly<Record<string, ViewFieldOverride>>,
): Issue<T>[] {
  const issues: Issue<T>[] = [];
  for (const field of fields) {
    const issue = validateFieldValue<T>(field, draft[field.key], overrides?.[field.key]);
    if (issue) issues.push(issue);
  }
  return issues;
}

export async function runRecordValidator<T>(
  validate: RecordValidator<T> | undefined,
  record: T,
  ctx: { mode: 'create' | 'update'; changed: ReadonlySet<keyof T> },
): Promise<Issue<T>[]> {
  if (!validate) return [];
  return await validate(record, ctx);
}

export function issuesForField<T>(issues: readonly Issue<T>[], key: string): Issue<T>[] {
  return issues.filter((issue) => issue.path[0] === key);
}
